import {
  Box,
  Divider,
  Paper,
  Stack,
  SvgIcon,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {memo} from 'react';
import DetectionsIcon from '../assets/Detections.jsx';
import HeatMapIcon from '../assets/Heatmap.jsx';
import {MODE, TIME_MODE} from '../constants/heatmap.js';
import {toFixed} from '../util/math.js';
import SliderFilter from './SliderFilter.jsx';

/**
 * The map toolbar is the toolbar on the top of the main collection map.
 * It allows switching between detections and heatmap.
 * It has a time toggle for showing either a specific layer or all time periods.
 * It also has the confidence score filtering if the data has scores.
 * And it allows going into the Change View by drawing a box.
 */
const MapToolbar = ({
  children,
  disabled,
  handleFilterChange,
  filterValues,
  defaultFilterValues,
  filterDisabled,
  mode,
  onModeChange,
  onTimeModeChange,
  timeMode,
}) => {
  return (
    <Paper
      elevation={2}
      square
      className="pl-mode-dark"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        paddingBlock: 1,
        paddingInline: 2,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'space-evenly',
          width: '100%',
        }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Stack sx={{gap: 1, width: 100}}>
          <Typography variant="body2">Visualization</Typography>
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            onChange={(_, newValue) => {
              onModeChange(newValue);
            }}
            value={mode}
          >
            <ToggleButton
              value={MODE.HEATMAP}
              title="Show Heatmap"
              disabled={disabled}
            >
              <SvgIcon>
                <HeatMapIcon />
              </SvgIcon>
            </ToggleButton>
            <ToggleButton
              title="Show Detections"
              value={MODE.DETECTIONS}
              disabled={disabled}
            >
              <SvgIcon>
                <DetectionsIcon />
              </SvgIcon>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack sx={{gap: 1, width: 100}}>
          <Typography variant="body2" sx={{width: '100%'}} noWrap>
            Show all time
          </Typography>
          <Switch
            checked={timeMode === TIME_MODE.ALL}
            color="primary"
            onChange={onTimeModeChange}
          />
        </Stack>

        <Stack sx={{width: '75%', minWidth: 200}}>
          <Typography variant="body2">Confidence</Typography>
          <Box sx={{maxWidth: 350, marginInline: 2}}>
            <SliderFilter
              min={0}
              max={1}
              step={0.01}
              size="small"
              initialValue={filterValues.confidence}
              defaultValue={defaultFilterValues.confidence}
              disabled={filterDisabled}
              id="confidence"
              onChangeCommitted={handleFilterChange}
              valueLabelDisplay="auto"
              valueLabelFormat={value => `${toFixed(value * 100, 2)}%`}
              marks={[
                {
                  value: 0,
                  label: '0%',
                },
                {
                  value: 1,
                  label: '100%',
                },
              ]}
            />
          </Box>
        </Stack>
      </Stack>
      <Stack sx={{marginLeft: 'auto', alignItems: 'center', flexShrink: 0}}>
        {!!children && <>{children}</>}
      </Stack>
    </Paper>
  );
};

MapToolbar.propTypes = {
  children: PropTypes.any,
  defaultFilterValues: PropTypes.object,
  disabled: PropTypes.bool,
  filterDisabled: PropTypes.bool,
  filterValues: PropTypes.object,
  handleFilterChange: PropTypes.func,
  mode: PropTypes.string,
  onModeChange: PropTypes.func,
  onTimeModeChange: PropTypes.func,
  timeMode: PropTypes.string,
};

export default memo(MapToolbar);
